import { useRef, useState } from "react";
import { useIsMutating, useMutation, useMutationState } from "@tanstack/react-query";
import type { Property } from "@shared/schema";
import { PropertyGroupSelect } from "@/components/property-group-select";
import { isUnauthorizedError, isImpersonationCancelled, parseErrorMessage, queryClient } from "@/lib/queryClient";
import { savePropertyGroup, UnconfirmedPropertyGroupSave } from "@/lib/property-group-save";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

export function PropertyDetailGroup({ property, readOnly, busy, onSessionExpired }: {
  property: Property;
  readOnly: boolean;
  busy: boolean;
  onSessionExpired: () => void;
}) {
  const { toast } = useToast();
  const lock = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const mutationKey = ["property-group-save", property.id];
  // The detail page remounts on navigation. An in-flight/uncertain write must
  // still block a second group selection when the host returns to this page.
  const pendingCount = useIsMutating({ mutationKey, exact: true });
  const failures = useMutationState({
    filters: { mutationKey, exact: true, status: "error" },
    select: (entry) => entry.state.error,
  });
  const uncertainFailure = failures.find((failure) => failure instanceof UnconfirmedPropertyGroupSave);
  const unconfirmed = !!uncertainFailure;
  const displayError = error ?? uncertainFailure?.message;
  const mutation = useMutation({
    mutationKey,
    retry: false,
    mutationFn: (group: string) => savePropertyGroup(property.id, group),
    onSuccess: async (updated) => {
      // Cancel pre-save reads before publishing the confirmed result. Patch only
      // the group so detail-only fields and unrelated concurrent edits survive.
      await queryClient.cancelQueries({ queryKey: ["/api/app/properties"] });
      queryClient.setQueryData<Property>(["/api/app/properties", property.id], (old) =>
        old ? { ...old, propertyGroup: updated.propertyGroup } : old);
      queryClient.setQueryData<Property[]>(["/api/app/properties"], (old) =>
        old?.map((item) => item.id === updated.id ? { ...item, propertyGroup: updated.propertyGroup } : item));
      void queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Property group saved" });
    },
    onError: (failure) => {
      if (isImpersonationCancelled(failure)) return;
      if (isUnauthorizedError(failure)) {
        toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
        onSessionExpired();
        return;
      }
      const detail = parseErrorMessage(failure, failure instanceof UnconfirmedPropertyGroupSave
        ? failure.message : "Couldn't save the property group. Check your access and try again.");
      const message = detail === "Forbidden"
        ? "You don't have permission to change this property's group. Ask the owner for edit access."
        : detail;
      setError(message);
      toast({ title: "Property group not confirmed", description: message, variant: "destructive" });
    },
    onSettled: () => { lock.current = false; },
  });

  function change(group: string) {
    if (readOnly || busy || pendingCount > 0 || unconfirmed || lock.current || group === (property.propertyGroup || "")) return;
    lock.current = true;
    setError(null);
    mutation.mutate(group);
  }

  return (
    <div aria-busy={mutation.isPending}>
      <PropertyGroupSelect
        value={mutation.isPending ? mutation.variables ?? "" : property.propertyGroup || ""}
        onChange={change}
        disabled={readOnly || busy || pendingCount > 0 || mutation.isPending || unconfirmed}
        placeholder="Select a group"
        allowNone
        testId="select-detail-group"
        className="text-sm"
      />
      {(mutation.isPending || pendingCount > 0) && <p role="status" className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Saving group…</p>}
      {displayError && <p role="alert" className="mt-1 text-xs text-destructive">{displayError}</p>}
    </div>
  );
}
